import type { ReactNode } from 'react'
import { useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { TableScrollWrapper } from './TableScrollWrapper'
import { TOUCH_TARGET_MIN_PX } from './layoutConstants'

export type ResponsiveTableColumn<T> = {
  id: string
  header: ReactNode
  /** Desktop table cell */
  renderCell: (row: T) => ReactNode
  /** Optional mobile card label; defaults to header */
  cardLabel?: ReactNode
}

export type ResponsiveTableProps<T> = {
  rows: T[]
  columns: ResponsiveTableColumn<T>[]
  getRowId: (row: T) => string
  /** md+ table, xs cards */
  breakpoint?: 'md' | 'sm'
  /** Below breakpoint: cards first; user opens full table in a horizontal scroller (admin lists). */
  expandTableOnMobile?: boolean
  emptyMessage?: ReactNode
  'data-testid'?: string
}

export function ResponsiveTable<T>({
  rows,
  columns,
  getRowId,
  breakpoint = 'md',
  expandTableOnMobile = false,
  emptyMessage = 'No rows',
  'data-testid': testId = 'responsive-table',
}: ResponsiveTableProps<T>) {
  const theme = useTheme()
  const isTable = useMediaQuery(theme.breakpoints.up(breakpoint), { noSsr: true })
  const [mobileTableOpen, setMobileTableOpen] = useState(false)

  const tableMarkup = (
    <TableScrollWrapper data-testid={`${testId}-table`}>
      <Table size="small" data-testid={testId}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.id}>{col.header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={getRowId(row)}>
              {columns.map((col) => (
                <TableCell key={col.id}>{col.renderCell(row)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableScrollWrapper>
  )

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid={`${testId}-empty`}>
        {emptyMessage}
      </Typography>
    )
  }

  if (!isTable) {
    return (
      <Stack spacing={1.5}>
        <Stack spacing={1.5} data-testid={`${testId}-cards`}>
          {rows.map((row) => (
            <Card key={getRowId(row)} variant="outlined">
              <CardContent>
                <Stack spacing={0.75}>
                  {columns.map((col) => (
                    <Box key={col.id}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {col.cardLabel ?? col.header}
                      </Typography>
                      <Typography variant="body2" component="div">
                        {col.renderCell(row)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
        {expandTableOnMobile && (
          <Box>
            {!mobileTableOpen ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setMobileTableOpen(true)}
                aria-expanded={false}
                sx={{ minHeight: TOUCH_TARGET_MIN_PX }}
                data-testid={`${testId}-open-table`}
              >
                Open table
              </Button>
            ) : (
              <Stack spacing={1}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setMobileTableOpen(false)}
                  aria-expanded
                  sx={{ minHeight: TOUCH_TARGET_MIN_PX }}
                  data-testid={`${testId}-close-table`}
                >
                  Hide table
                </Button>
                {tableMarkup}
              </Stack>
            )}
          </Box>
        )}
      </Stack>
    )
  }

  return tableMarkup
}

export default ResponsiveTable
